import { create } from 'zustand';
import { ApiError, getServerTimeOffsetMs } from '@/api/client';
import { isGatewayFailure } from '@/stores/connection';
import { changeOutbox, fillTokens, forgetOldSent, readOutbox, refsOf, type Lane, type Operation, type OutboxHolder } from './outbox';
import { piecesOf } from './pieces';

/**
 * Sending what the outbox holds (.docs/023, step 4).
 *
 * Oldest first, in lanes: a refusal stops its own lane and nothing else. What
 * the server's answer means is the table in .docs/023 — an answer that is not
 * a decision (no line, a server fault, "slow down", "still being sent") is
 * waited out and never shown as a failure; only a decision stops a lane.
 */

/** What a failed attempt means for the work. */
export type Verdict =
  /** Not a decision: send again later. */
  | 'wait'
  /** The session has ended: nothing goes until the person signs in again. */
  | 'sign-in'
  /** This build is too old to send: nothing goes until the page is reloaded. */
  | 'too-old'
  /** The server decided no: a person has to look. */
  | 'refused';

export function verdictOf(error: unknown): Verdict {
  if (!(error instanceof ApiError)) return 'wait';
  const code = (error.body as { code?: unknown } | null)?.code;
  const status = error.status;
  if (status === 0 || status === 408 || status === 429 || status >= 500) return 'wait';
  if (status === 409 && code === 'IDEMPOTENCY_IN_FLIGHT') return 'wait';
  if (status === 401) return 'sign-in';
  if (status === 403 && code === 'CLIENT_TOO_OLD') return 'too-old';
  return 'refused';
}

/**
 * The server could not be reached at all: no line, or the proxy saying the
 * server is not there. Not a request called off on this side, and not a
 * server that was reached and failed — those are answers of a kind.
 */
export function noConnection(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status === 0) return error.message !== 'Cancelled';
  return isGatewayFailure(error.status);
}

/** How one request is actually sent. The API client's, outside tests. */
export type Send = (
  method: Operation['method'],
  path: string,
  body: unknown,
  headers: Record<string, string>,
) => Promise<unknown>;

export interface Pass {
  sent: number;
  /** What went, as it now stands, and what the server said no to, in this pass. */
  sentOps?: Operation[];
  refusedOps?: Operation[];
  /** Why the pass stopped short of the end, if it did. */
  stopped: Exclude<Verdict, 'refused'> | null;
}

/**
 * One pass over the outbox: everything that can go, goes, oldest first.
 *
 * Every change of state goes through `changeOutbox`, so a tab saving new work
 * while this runs never has it written over.
 */
export async function sendPass(store: OutboxHolder, send: Send, now: () => number = Date.now): Promise<Pass> {
  const ops = await readOutbox(store);
  /**
   * Per lane: work saved up to this time waits behind something that cannot
   * go. Work saved after a refusal goes: it was typed with the refusal already
   * known, against what the server holds now, and stands on nothing refused.
   * Work saved behind something merely not sent yet waits, in its order.
   */
  const heldUntil = new Map<Lane, number>();
  /** What cannot go this pass, and from when later work may overtake it. */
  const stuckAt = new Map<string, number>();
  const hold = (op: Operation, at: number) => {
    stuckAt.set(op.id, at);
    heldUntil.set(op.lane, Math.max(heldUntil.get(op.lane) ?? -Infinity, at));
  };
  const clock = () => now() + getServerTimeOffsetMs();
  const sentIds = new Map(ops.filter((o) => o.state === 'sent').map((o) => [o.id, o.resultId]));
  const known = new Set(ops.map((o) => o.id));
  let sent = 0;
  const sentOps: Operation[] = [];
  const refusedOps: Operation[] = [];

  for (const planned of ops) {
    if (planned.state === 'sent') continue;
    if (planned.state === 'refused') {
      // Refused before the time was kept: from when it was saved.
      hold(planned, planned.refusedAt ?? planned.createdAt);
      continue;
    }
    const held = heldUntil.get(planned.lane);
    if (held !== undefined && planned.createdAt <= held) {
      stuckAt.set(planned.id, held);
      continue;
    }

    let path = planned.path;
    let body = planned.body;
    const refs = refsOf(planned);
    if (refs.length) {
      const missing = refs.filter((ref) => !known.has(ref));
      if (missing.length) {
        // What it names was thrown away, or forgotten: it can never be
        // filled in. A person has to choose again.
        const at = clock();
        await update(store, planned.id, (o) => {
          o.state = 'refused';
          o.lastError = 'It names something saved on this PC that has since been thrown away. Fix it and choose again.';
          o.httpStatus = null;
          o.refusedAt = at;
          return true;
        });
        hold(planned, at);
        continue;
      }
      if (refs.some((ref) => !sentIds.get(ref))) {
        // What it stands on has not gone yet, so neither can it, and nothing
        // after it in its lane may overtake it — unless what it stands on is
        // stuck behind a refusal, which work saved since need not wait for.
        const stuck = refs.filter((ref) => !sentIds.get(ref)).map((ref) => stuckAt.get(ref) ?? Infinity);
        hold(planned, Math.max(planned.createdAt, ...stuck));
        continue;
      }
      if (planned.dependsOn) path = path.replace('{dep}', encodeURIComponent(sentIds.get(planned.dependsOn)!));
      path = fillTokens(path, sentIds) as string;
      if (body !== undefined) body = fillTokens(body, sentIds);
    }

    // Read again under the lock: another tab may have changed it since.
    const op = await update(store, planned.id, (o) => {
      if (o.state !== 'waiting' && o.state !== 'sending') return false;
      o.state = 'sending';
      o.attempts += 1;
      return true;
    });
    if (!op) continue;

    try {
      const result = await send(op.method, path, body, {
        'Idempotency-Key': op.id,
        'X-Client-Recorded-At': new Date(op.createdAt).toISOString(),
      });
      const resultId = idOf(result);
      const done = await update(store, op.id, (o) => {
        Object.assign(o, { state: 'sent', sentAt: now(), result, resultId, lastError: null, httpStatus: null });
        return true;
      });
      if (resultId) sentIds.set(op.id, resultId);
      if (done) sentOps.push(done);
      sent += 1;
    } catch (error) {
      const verdict = verdictOf(error);
      if (verdict !== 'refused') {
        await update(store, op.id, (o) => {
          // Only what this pass marked: never undo an answer written meanwhile.
          if (o.state !== 'sending') return false;
          o.state = 'waiting';
          return true;
        });
        return { sent, sentOps, refusedOps, stopped: verdict };
      }
      const at = clock();
      const refused = await update(store, op.id, (o) => {
        o.state = 'refused';
        o.lastError = error instanceof Error ? error.message : 'The server refused it.';
        o.httpStatus = error instanceof ApiError ? error.status : null;
        o.refusedAt = at;
        return true;
      });
      if (refused) refusedOps.push(refused);
      hold(op, at);
    }
  }

  await changeOutbox(store, (all) => ({ ops: forgetOldSent(all, now()), result: undefined }));
  return { sent, sentOps, refusedOps, stopped: null };
}

async function update(store: OutboxHolder, id: string, change: (op: Operation) => boolean): Promise<Operation | null> {
  return changeOutbox(store, (ops) => {
    const op = ops.find((o) => o.id === id);
    if (!op || !change(op)) return { ops, result: null };
    return { ops, result: { ...op } };
  });
}

/** The id of what a create made. Plenty of operations make nothing; that is fine. */
export function idOf(result: unknown): string | null {
  if (result !== null && typeof result === 'object' && 'id' in result) {
    const id = (result as { id: unknown }).id;
    if (typeof id === 'string' || typeof id === 'number') return String(id);
  }
  return null;
}

// ---------------------------------------------------------------- status

export interface OutboxStatus {
  /** Saves made here, not yet with the server: a receipt and its post are one. */
  waiting: number;
  /** Saves the server said no to; a person has to look. */
  refused: number;
  /** Why nothing is going, when something is in the way of all of it. */
  held: Exclude<Verdict, 'refused'> | null;
  /** When the server last took something. */
  lastSentAt: number | null;
  /** A pass is going out now, with work waiting. */
  sending: boolean;
}

export const useOutboxStatus = create<OutboxStatus>(() => ({
  waiting: 0,
  refused: 0,
  held: null,
  lastSentAt: null,
  sending: false,
}));

export function statusOf(ops: readonly Operation[]): Omit<OutboxStatus, 'held' | 'sending'> {
  let lastSentAt: number | null = null;
  for (const op of ops) if (op.sentAt && (lastSentAt === null || op.sentAt > lastSentAt)) lastSentAt = op.sentAt;
  // Counted as people saved them, not as requests: one Stock IN with its post
  // is "1 waiting", not 2.
  const pieces = piecesOf(ops);
  return {
    waiting: pieces.filter((p) => p.state === 'waiting').length,
    refused: pieces.filter((p) => p.state === 'refused').length,
    lastSentAt,
  };
}
